import {
  AlertTriangle,
  ArrowLeft,
  Check,
  ChevronRight,
  Database,
  Download,
  FileCheck2,
  FileSpreadsheet,
  GitMerge,
  Loader2,
  Pencil,
  RefreshCw,
  RotateCcw,
  ShieldCheck,
  UploadCloud,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ImportCommandResult,
  ImportErrorRecord,
  ImportJobRecord,
  ImportPreview,
  ImportReconciliation,
  ImportRowRecord,
  MappingInference,
  productRevivalImportService,
} from "../../services/product-revival-import.service";

type View =
  | "upload"
  | "mapping"
  | "validation"
  | "merge"
  | "reconcile"
  | "result";

const views: Array<{ key: View; label: string }> = [
  { key: "upload", label: "Upload" },
  { key: "mapping", label: "Map" },
  { key: "validation", label: "Validate" },
  { key: "merge", label: "Merge" },
  { key: "reconcile", label: "Review" },
  { key: "result", label: "Result" },
];

const canonicalFields = [
  "name",
  "sku",
  "barcode",
  "supplier",
  "purchaseReference",
  "manufacturer",
  "category",
  "genericName",
  "strength",
  "dosageForm",
  "scheduleCategory",
  "hsn",
  "gst",
  "pack",
  "unit",
  "quantity",
  "sourceBaseQuantity",
  "openingBaseQuantity",
  "sealedPackQuantity",
  "freeQuantity",
  "sourcePurchaseQuantity",
  "sourceStockValue",
  "sourceDrugName",
  "batchNumber",
  "expiry",
  "mrp",
  "purchasePrice",
  "sellingPrice",
  "quantityUom",
  "priceUom",
  "packGrammar",
  "conversionNumerator",
  "conversionDenominator",
  "purchaseTaxBasis",
  "retailTaxBasis",
  "taxBasis",
  "allowLooseSale",
];

const correctionFields = [
  ["name", "Product name", "text"],
  ["sku", "SKU", "text"],
  ["barcode", "Barcode", "text"],
  ["quantity", "Quantity (packs)", "number"],
  ["openingBaseQuantity", "Opening quantity (base units)", "number"],
  ["sealedPackQuantity", "Sealed pack quantity", "number"],
  ["looseBaseQuantity", "Loose base-unit quantity", "number"],
  ["freeQuantity", "Free quantity (packs)", "number"],
  ["pack", "Pack description", "text"],
  ["packMultiplier", "Base units per pack", "number"],
  ["quantityUom", "Quantity UOM", "text"],
  ["priceUom", "Price UOM", "text"],
  ["packGrammar", "Pack grammar", "text"],
  ["conversionNumerator", "Conversion numerator", "number"],
  ["conversionDenominator", "Conversion denominator", "number"],
  ["purchaseTaxBasis", "Purchase tax basis (INCLUSIVE/EXCLUSIVE)", "text"],
  ["retailTaxBasis", "Retail tax basis (INCLUSIVE/EXCLUSIVE)", "text"],
  ["taxBasis", "Legacy tax basis (applies to both)", "text"],
  ["allowLooseSale", "Allow loose sale (true/false)", "text"],
  ["batchNumber", "Batch number", "text"],
  ["expiry", "Expiry (ISO date)", "text"],
  ["mrpPaise", "MRP (paise)", "number"],
  ["purchasePricePaise", "Purchase price (paise)", "number"],
  ["sellingPricePaise", "Selling price (paise)", "number"],
  ["gstBps", "GST (basis points; 500 = 5%)", "number"],
  ["hsn", "HSN", "text"],
] as const;

function errorMessage(error: unknown) {
  const candidate = error as {
    response?: { data?: { message?: string; code?: string } };
    message?: string;
  };
  return (
    candidate.response?.data?.message ??
    candidate.response?.data?.code?.replace(/_/g, " ") ??
    candidate.message ??
    "The import request could not be completed"
  );
}

function formatPaise(value: string) {
  const paise = Number(value);
  if (!Number.isFinite(paise)) return value;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(paise / 100);
}

function initialView(job: ImportJobRecord): View {
  if (job.status === "ACTIVATED" || job.status === "REVERSED") return "result";
  if (job.status === "RECONCILED") return "reconcile";
  if (job.status === "READY_TO_COMMIT") return "validation";
  return "mapping";
}

function Metric({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </p>
      <p className="mt-1 text-2xl font-bold text-slate-900">{value}</p>
    </div>
  );
}

export function ImportProductsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fileInput = useRef<HTMLInputElement>(null);
  const jobId = searchParams.get("job");
  const requestedView = searchParams.get("view") as View | null;
  const [view, setViewState] = useState<View>(requestedView ?? "upload");
  const [job, setJob] = useState<ImportJobRecord | null>(null);
  const [mapping, setMapping] = useState<MappingInference | null>(null);
  const [normalizationDraft, setNormalizationDraft] = useState("");
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [reconciliation, setReconciliation] =
    useState<ImportReconciliation | null>(null);
  const [result, setResult] = useState<ImportCommandResult | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [decisions, setDecisions] = useState<
    Record<string, "MERGED" | "IGNORED" | "REJECTED">
  >({});
  const [filteredErrors, setFilteredErrors] = useState<ImportErrorRecord[]>([]);
  const [severity, setSeverity] = useState("");
  const [field, setField] = useState("");
  const [editingRow, setEditingRow] = useState<ImportRowRecord | null>(null);
  const [correction, setCorrection] = useState<Record<string, string>>({});
  const [undoReason, setUndoReason] = useState("");

  const setView = useCallback(
    (next: View, nextJobId = jobId) => {
      setViewState(next);
      const params = new URLSearchParams();
      if (nextJobId) params.set("job", nextJobId);
      if (next !== "upload") params.set("view", next);
      setSearchParams(params, { replace: true });
    },
    [jobId, setSearchParams],
  );

  const loadJob = useCallback(async (id: string) => {
    const loaded = await productRevivalImportService.job(id);
    setJob(loaded);
    setFilteredErrors(loaded.errors ?? []);
    return loaded;
  }, []);

  const acceptInference = useCallback((inference: MappingInference) => {
    setMapping(inference);
    setNormalizationDraft(
      JSON.stringify(inference.normalization ?? { version: 1 }, null, 2),
    );
  }, []);

  useEffect(() => {
    if (!jobId) {
      setViewState("upload");
      return;
    }
    let active = true;
    (async () => {
      try {
        setBusy("Restoring import state");
        const loaded = await loadJob(jobId);
        if (!active) return;
        const next = requestedView ?? initialView(loaded);
        if (loaded.status === "ACTIVATED" || loaded.status === "REVERSED") {
          setViewState("result");
        } else {
          setViewState(next);
        }
        if (next === "mapping" && loaded.status === "UPLOADED") {
          acceptInference(
            await productRevivalImportService.inferMapping(jobId),
          );
        }
        if (next === "merge") {
          setPreview(await productRevivalImportService.preview(jobId));
        }
        if (
          next === "reconcile" &&
          ["READY_TO_COMMIT", "RECONCILED"].includes(loaded.status)
        ) {
          setReconciliation(
            await productRevivalImportService.getReconciliation(jobId),
          );
        }
      } catch (caught) {
        if (active) setError(errorMessage(caught));
      } finally {
        if (active) setBusy(null);
      }
    })();
    return () => {
      active = false;
    };
  }, [acceptInference, jobId, loadJob, requestedView]);

  const currentIndex = views.findIndex((item) => item.key === view);
  const unresolved = useMemo(
    () =>
      preview?.proposals.filter((proposal) => proposal.requiresDecision) ?? [],
    [preview],
  );

  const upload = async (selected: File) => {
    if (selected.size > 50 * 1024 * 1024) {
      setError("The source file must be 50 MiB or smaller");
      return;
    }
    try {
      setFile(selected);
      setError(null);
      setBusy("Uploading and fingerprinting the source file");
      const uploaded = await productRevivalImportService.upload(selected);
      setJob(uploaded);
      const inferred = await productRevivalImportService.inferMapping(
        uploaded.id,
      );
      acceptInference(inferred);
      setView("mapping", uploaded.id);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(null);
    }
  };

  const saveMapping = async () => {
    if (!jobId || !mapping) return;
    try {
      setBusy("Saving mapping profile and validating source rows");
      setError(null);
      const normalization = normalizationDraft.trim()
        ? (JSON.parse(normalizationDraft) as Record<string, unknown>)
        : undefined;
      const sourceName =
        job?.file?.originalFilename ?? file?.name ?? "Pharmacy import";
      await productRevivalImportService.saveAndApplyProfile(
        jobId,
        sourceName.replace(/\.[^.]+$/, ""),
        mapping.mapping,
        normalization,
        mapping.matchedTemplate?.sourceSystem,
      );
      const staged = await productRevivalImportService.parse(jobId);
      setJob(staged);
      setFilteredErrors(staged.errors ?? []);
      setView("validation");
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(null);
    }
  };

  const revalidate = async () => {
    if (!jobId) return;
    try {
      setBusy("Revalidating every staged row");
      setError(null);
      const staged = await productRevivalImportService.revalidate(jobId);
      setJob(staged);
      setFilteredErrors(staged.errors ?? []);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(null);
    }
  };

  const openCorrection = (row: ImportRowRecord) => {
    const normalized = row.normalizedRecord ?? {};
    setEditingRow(row);
    setCorrection(
      Object.fromEntries(
        correctionFields.map(([key]) => [
          key,
          normalized[key] === null || normalized[key] === undefined
            ? ""
            : String(normalized[key]),
        ]),
      ),
    );
  };

  const saveCorrection = async () => {
    if (!jobId || !editingRow) return;
    const numeric = new Set([
      "packMultiplier",
      "mrpPaise",
      "purchasePricePaise",
      "sellingPricePaise",
      "gstBps",
    ]);
    const boolean = new Set(["allowLooseSale", "isOtc"]);
    const values = Object.fromEntries(
      Object.entries(correction).map(([key, value]) => [
        key,
        value === ""
          ? null
          : numeric.has(key)
            ? Number(value)
            : boolean.has(key)
              ? value.trim().toLowerCase() === "true"
              : value,
      ]),
    );
    try {
      setBusy("Saving correction and revalidating all rows");
      setError(null);
      await productRevivalImportService.correctRow(
        jobId,
        editingRow.id,
        values,
      );
      setEditingRow(null);
      await loadJob(jobId);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(null);
    }
  };

  const loadMerge = async () => {
    if (!jobId) return;
    try {
      setBusy("Comparing staged rows with the live catalog");
      setError(null);
      const loaded = await productRevivalImportService.preview(jobId);
      setPreview(loaded);
      setDecisions({});
      setView("merge");
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(null);
    }
  };

  const reconcile = async () => {
    if (!jobId || !preview) return;
    const missing = unresolved.filter((proposal) => !decisions[proposal.rowId]);
    if (missing.length) {
      setError(
        `Choose a merge, reject, or ignore decision for ${missing.length} row(s)`,
      );
      return;
    }
    try {
      setBusy("Recording merge decisions and calculating reconciliation");
      setError(null);
      const decisionPayload = unresolved.map((proposal) => ({
        rowId: proposal.rowId,
        disposition: decisions[proposal.rowId],
        entityId:
          decisions[proposal.rowId] === "MERGED"
            ? proposal.product.id
            : undefined,
        reason:
          decisions[proposal.rowId] === "MERGED"
            ? "Exact catalog match selected in Import Center"
            : "Excluded in Import Center review",
      }));
      if (decisionPayload.length) {
        await productRevivalImportService.decide(jobId, decisionPayload);
      }
      const summary = await productRevivalImportService.reconcile(jobId);
      setReconciliation(summary);
      setView("reconcile");
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(null);
    }
  };

  const commit = async () => {
    if (!jobId || !reconciliation) return;
    if (
      !window.confirm(
        "Activate exactly these reconciled catalog and stock totals?",
      )
    ) {
      return;
    }
    try {
      setBusy("Activating the reconciled import atomically");
      setError(null);
      const committed = await productRevivalImportService.commit(jobId);
      setResult(committed);
      await loadJob(jobId);
      setView("result");
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(null);
    }
  };

  const undo = async () => {
    if (!jobId || !undoReason.trim()) {
      setError("Enter a reason before undoing this activation");
      return;
    }
    try {
      setBusy("Checking downstream use and preparing safe compensation");
      setError(null);
      const undone = await productRevivalImportService.undo(jobId, undoReason);
      setResult(undone);
      await loadJob(jobId);
      setUndoReason("");
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(null);
    }
  };

  const filterErrors = async () => {
    if (!jobId) return;
    try {
      setBusy("Filtering validation errors");
      setFilteredErrors(
        await productRevivalImportService.errors(jobId, {
          severity: severity || undefined,
          field: field || undefined,
        }),
      );
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(null);
    }
  };

  const reset = () => {
    setSearchParams({}, { replace: true });
    setViewState("upload");
    setJob(null);
    setMapping(null);
    setNormalizationDraft("");
    setPreview(null);
    setReconciliation(null);
    setResult(null);
    setFile(null);
    setError(null);
    if (fileInput.current) fileInput.current.value = "";
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6 pb-12">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <button
            type="button"
            onClick={() => navigate("/products")}
            className="mb-3 inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="h-4 w-4" /> Products
          </button>
          <h1 className="text-3xl font-bold tracking-tight text-slate-950">
            Import Center
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-600">
            Stage, validate, reconcile, and activate pharmacy catalog and
            opening stock with complete source lineage.
          </p>
        </div>
        {jobId && (
          <button
            type="button"
            onClick={reset}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            <UploadCloud className="h-4 w-4" /> New import
          </button>
        )}
      </div>

      <nav aria-label="Import progress" className="overflow-x-auto">
        <ol className="flex min-w-[680px] items-center rounded-xl border border-slate-200 bg-white px-4 py-3">
          {views.map((item, index) => (
            <li key={item.key} className="flex flex-1 items-center">
              <div
                aria-current={item.key === view ? "step" : undefined}
                className="flex items-center gap-2"
              >
                <span
                  className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold ${
                    index < currentIndex
                      ? "bg-emerald-600 text-white"
                      : index === currentIndex
                        ? "bg-blue-600 text-white"
                        : "bg-slate-100 text-slate-500"
                  }`}
                >
                  {index < currentIndex ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    index + 1
                  )}
                </span>
                <span
                  className={`text-sm font-semibold ${index === currentIndex ? "text-blue-700" : "text-slate-600"}`}
                >
                  {item.label}
                </span>
              </div>
              {index < views.length - 1 && (
                <ChevronRight className="ml-auto h-4 w-4 text-slate-300" />
              )}
            </li>
          ))}
        </ol>
      </nav>

      {busy && (
        <div
          role="status"
          className="flex items-center gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm font-medium text-blue-900"
        >
          <Loader2 className="h-5 w-5 animate-spin" /> {busy}
        </div>
      )}
      {error && (
        <div
          role="alert"
          className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <div className="flex-1">
            <p className="font-semibold">Action required</p>
            <p>{error}</p>
          </div>
          <button
            type="button"
            onClick={() => setError(null)}
            className="font-semibold"
          >
            Dismiss
          </button>
        </div>
      )}

      {view === "upload" && (
        <section className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div
              role="button"
              tabIndex={0}
              aria-label="Choose an import file"
              onClick={() => fileInput.current?.click()}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  fileInput.current?.click();
                }
              }}
              onDragOver={(event) => {
                event.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(event) => {
                event.preventDefault();
                setDragging(false);
                const selected = event.dataTransfer.files[0];
                if (selected) void upload(selected);
              }}
              className={`flex min-h-80 cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 text-center outline-none transition focus:ring-4 focus:ring-blue-100 ${
                dragging
                  ? "border-blue-500 bg-blue-50"
                  : "border-slate-300 bg-slate-50 hover:border-blue-400 hover:bg-blue-50/40"
              }`}
            >
              <input
                ref={fileInput}
                type="file"
                accept=".csv,.tsv,.xlsx,.xls"
                className="sr-only"
                onChange={(event) => {
                  const selected = event.target.files?.[0];
                  if (selected) void upload(selected);
                }}
              />
              <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-100 text-blue-700">
                <FileSpreadsheet className="h-8 w-8" />
              </span>
              <h2 className="mt-5 text-xl font-bold text-slate-900">
                Choose a pharmacy export
              </h2>
              <p className="mt-2 max-w-md text-sm text-slate-600">
                Drop a CSV, TSV, XLSX, or legacy HTML-XLS file here, or press
                Enter to browse. Maximum size 50 MiB.
              </p>
              <span className="mt-5 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-bold text-white">
                Browse files
              </span>
            </div>
          </div>
          <aside className="space-y-4 rounded-2xl bg-slate-950 p-6 text-white">
            <ShieldCheck className="h-9 w-9 text-emerald-400" />
            <h2 className="text-xl font-bold">Nothing goes live on upload</h2>
            <p className="text-sm leading-6 text-slate-300">
              The original file is fingerprinted and retained. Rows stay in a
              staging area until you review and activate the reconciled totals.
            </p>
            <ul className="space-y-3 text-sm text-slate-200">
              {[
                "Raw values and source coordinates remain available",
                "Invalid data is rejected with a specific reason",
                "Purchase cost and retail price keep separate GST bases",
                "MRP and selling price activate as GST-inclusive values",
                "Missing selling price defaults to MRP with recorded provenance",
                "Exact catalog matches require an explicit merge decision",
                "Activation is atomic and safe to retry",
              ].map((text) => (
                <li key={text} className="flex gap-2">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
                  {text}
                </li>
              ))}
            </ul>
          </aside>
        </section>
      )}

      {view === "mapping" && mapping && (
        <section className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Metric
              label="Detected format"
              value={job?.file?.detectedFormat ?? "—"}
            />
            <Metric
              label="Columns mapped"
              value={`${(mapping.coverageBps / 100).toFixed(0)}%`}
            />
            <Metric
              label="Average confidence"
              value={`${(mapping.confidenceBps / 100).toFixed(0)}%`}
            />
          </div>
          {mapping.matchedTemplate && (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-emerald-950">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-emerald-700">
                    Versioned template matched
                  </p>
                  <h2 className="mt-1 text-lg font-bold">
                    {mapping.matchedTemplate.name}
                  </h2>
                  <p className="mt-1 text-sm">
                    Schema confidence{" "}
                    {(mapping.matchedTemplate.confidenceBps / 100).toFixed(0)}%
                  </p>
                </div>
                <code className="rounded-lg bg-white/70 px-3 py-2 text-xs">
                  {mapping.schemaFingerprint?.slice(0, 16)}
                </code>
              </div>
              <ul className="mt-4 grid gap-2 text-sm lg:grid-cols-2">
                {mapping.matchedTemplate.assumptions.map((assumption) => (
                  <li key={assumption} className="flex gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0" />
                    {assumption}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 px-6 py-4">
              <h2 className="text-lg font-bold text-slate-900">
                Confirm column mapping
              </h2>
              <p className="mt-1 text-sm text-slate-600">
                Low-confidence fields are highlighted. Overrides are saved as a
                new versioned profile for this source.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-6 py-3">Source column</th>
                    <th className="px-6 py-3">MediCorePay field</th>
                    <th className="px-6 py-3">Confidence</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {mapping.suggestions.map((suggestion) => (
                    <tr key={suggestion.sourceHeader}>
                      <td className="px-6 py-3 font-semibold text-slate-800">
                        {suggestion.sourceHeader}
                      </td>
                      <td className="px-6 py-3">
                        <label
                          className="sr-only"
                          htmlFor={`map-${suggestion.sourceHeader}`}
                        >
                          Map {suggestion.sourceHeader}
                        </label>
                        <select
                          id={`map-${suggestion.sourceHeader}`}
                          value={mapping.mapping[suggestion.sourceHeader] ?? ""}
                          onChange={(event) =>
                            setMapping((current) =>
                              current
                                ? {
                                    ...current,
                                    mapping: {
                                      ...current.mapping,
                                      [suggestion.sourceHeader]:
                                        event.target.value || null,
                                    },
                                  }
                                : current,
                            )
                          }
                          className="w-full max-w-xs rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                        >
                          <option value="">Do not import</option>
                          {canonicalFields.map((target) => (
                            <option key={target} value={target}>
                              {target}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-6 py-3">
                        <span
                          className={`rounded-full px-2.5 py-1 text-xs font-bold ${suggestion.requiresReview ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}
                        >
                          {(suggestion.confidenceBps / 100).toFixed(0)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <details className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <summary className="cursor-pointer font-bold text-slate-900">
              Advanced deterministic profile rules
            </summary>
            <p className="mt-2 text-sm text-slate-600">
              Configure audited defaults, coalescing, generated identifiers, and
              pack-to-base UOM rules. Executable scripts are not accepted.
            </p>
            <label className="mt-4 block text-xs font-bold uppercase tracking-wide text-slate-600">
              Versioned normalization JSON
              <textarea
                value={normalizationDraft}
                onChange={(event) => setNormalizationDraft(event.target.value)}
                rows={16}
                spellCheck={false}
                className="mt-2 w-full rounded-xl border border-slate-300 bg-slate-950 p-4 font-mono text-xs leading-5 text-slate-100 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />
            </label>
          </details>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => void saveMapping()}
              disabled={Boolean(busy)}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              Save profile & validate <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </section>
      )}

      {view === "validation" && job && (
        <section className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <Metric label="Source rows" value={job.totalRows} />
            <Metric
              label="Products retained"
              value={
                job.rows.filter((row) => row.catalogDisposition !== "REJECTED")
                  .length
              }
            />
            <Metric
              label="Saleable stock lines"
              value={
                job.rows.filter(
                  (row) => row.inventoryDisposition === "AVAILABLE",
                ).length
              }
            />
            <Metric
              label="Expired tracked"
              value={
                job.rows.filter((row) => row.inventoryDisposition === "EXPIRED")
                  .length
              }
            />
            <Metric
              label="Inventory rejected"
              value={
                job.rows.filter(
                  (row) => row.inventoryDisposition === "REJECTED",
                ).length
              }
            />
          </div>
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-col gap-3 border-b border-slate-200 px-6 py-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-900">
                  Validation results
                </h2>
                <p className="mt-1 text-sm text-slate-600">
                  Correct normalized values here; the raw source remains
                  unchanged.
                </p>
              </div>
              <div className="flex flex-wrap items-end gap-2">
                <label className="text-xs font-semibold text-slate-600">
                  Severity
                  <select
                    value={severity}
                    onChange={(event) => setSeverity(event.target.value)}
                    className="ml-2 rounded-lg border border-slate-300 px-2 py-2 text-sm"
                  >
                    <option value="">All</option>
                    <option>WARNING</option>
                    <option>ERROR</option>
                    <option>FATAL</option>
                  </select>
                </label>
                <label className="text-xs font-semibold text-slate-600">
                  Field
                  <input
                    value={field}
                    onChange={(event) => setField(event.target.value)}
                    className="ml-2 w-32 rounded-lg border border-slate-300 px-2 py-2 text-sm"
                  />
                </label>
                <button
                  type="button"
                  onClick={() => void filterErrors()}
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold hover:bg-slate-50"
                >
                  Filter
                </button>
                <button
                  type="button"
                  onClick={() =>
                    jobId &&
                    void productRevivalImportService.downloadErrors(jobId)
                  }
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold hover:bg-slate-50"
                >
                  <Download className="h-4 w-4" /> CSV
                </button>
              </div>
            </div>
            {filteredErrors.length === 0 ? (
              <div className="flex flex-col items-center px-6 py-12 text-center">
                <FileCheck2 className="h-10 w-10 text-emerald-600" />
                <p className="mt-3 font-bold text-slate-900">
                  No validation errors in this view
                </p>
                <p className="mt-1 text-sm text-slate-600">
                  Accepted rows are ready for catalog comparison.
                </p>
              </div>
            ) : (
              <div className="max-h-[460px] overflow-auto">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="sticky top-0 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-5 py-3">Source</th>
                      <th className="px-5 py-3">Field</th>
                      <th className="px-5 py-3">Issue</th>
                      <th className="px-5 py-3">Raw value</th>
                      <th className="px-5 py-3">
                        <span className="sr-only">Action</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredErrors.slice(0, 200).map((item) => {
                      const row = job.rows.find(
                        (candidate) => candidate.id === item.rowId,
                      );
                      return (
                        <tr key={item.id}>
                          <td className="whitespace-nowrap px-5 py-3 font-mono text-xs">
                            {row?.sourceCoordinates ??
                              item.row?.sourceCoordinates ??
                              "File"}
                          </td>
                          <td className="px-5 py-3">{item.field ?? "Row"}</td>
                          <td className="px-5 py-3">
                            <p className="font-semibold text-red-700">
                              {item.code}
                            </p>
                            <p className="text-slate-600">{item.message}</p>
                          </td>
                          <td className="max-w-xs truncate px-5 py-3 font-mono text-xs text-slate-600">
                            {item.rawValue ?? "—"}
                          </td>
                          <td className="px-5 py-3 text-right">
                            <button
                              type="button"
                              disabled={!row?.normalizedRecord}
                              onClick={() => row && openCorrection(row)}
                              className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-bold hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                              <Pencil className="h-3.5 w-3.5" /> Correct
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="flex flex-wrap justify-between gap-3">
            <button
              type="button"
              onClick={() => void revalidate()}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold hover:bg-slate-50"
            >
              <RefreshCw className="h-4 w-4" /> Revalidate
            </button>
            <button
              type="button"
              onClick={() => void loadMerge()}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-blue-700"
            >
              Review merge proposals <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </section>
      )}

      {view === "merge" && preview && (
        <section className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-3">
            <Metric
              label="Create proposals"
              value={
                preview.proposals.filter(
                  (item) => item.proposedAction === "CREATE",
                ).length
              }
            />
            <Metric
              label="Auto-merged exact matches"
              value={
                preview.proposals.filter(
                  (item) => item.proposedAction === "MERGE_AUTO",
                ).length
              }
            />
            <Metric label="Manual decisions" value={unresolved.length} />
            <Metric
              label="Catalog rejected"
              value={
                preview.proposals.filter(
                  (item) => item.catalogDisposition === "REJECTED",
                ).length
              }
            />
            <Metric
              label="Expired stock lines"
              value={
                preview.proposals.filter(
                  (item) => item.inventoryDisposition === "EXPIRED",
                ).length
              }
            />
          </div>
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 px-6 py-4">
              <h2 className="text-lg font-bold text-slate-900">
                Catalog merge preview
              </h2>
              <p className="mt-1 text-sm text-slate-600">
                Unique 100% identifier matches are auto-merged. Only conflicting
                matches require a manual decision.
              </p>
            </div>
            <div className="max-h-[560px] overflow-auto">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="sticky top-0 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-5 py-3">Source</th>
                    <th className="px-5 py-3">Proposal</th>
                    <th className="px-5 py-3">Product</th>
                    <th className="px-5 py-3">Lot detail</th>
                    <th className="px-5 py-3">Decision</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {preview.proposals.map((proposal) => (
                    <tr key={proposal.rowId}>
                      <td className="px-5 py-3 font-mono text-xs">
                        {proposal.sourceCoordinates}
                      </td>
                      <td className="px-5 py-3">
                        <span
                          className={`rounded-full px-2.5 py-1 text-xs font-bold ${proposal.proposedAction.includes("MERGE") ? "bg-amber-100 text-amber-800" : proposal.proposedAction === "CREATE" ? "bg-blue-100 text-blue-800" : "bg-slate-100 text-slate-700"}`}
                        >
                          {proposal.proposedAction
                            .replace("_PROPOSED", "")
                            .replace("_AUTO", "")}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <p className="font-semibold text-slate-900">
                          {proposal.product.name ??
                            proposal.product.create?.name ??
                            "Unnamed"}
                        </p>
                        <p className="text-xs text-slate-500">
                          {proposal.product.sku ??
                            proposal.product.create?.sku ??
                            "No identifier"}
                        </p>
                      </td>
                      <td className="px-5 py-3 text-xs text-slate-600">
                        <p>
                          Catalog:{" "}
                          {proposal.catalogDisposition.replace("_", " ")}
                        </p>
                        <p>
                          Inventory:{" "}
                          {proposal.inventoryDisposition.replace("_", " ")}
                        </p>
                        <p>Pack: {proposal.pack?.action ?? "—"}</p>
                        <p>
                          Batch:{" "}
                          {proposal.batch?.number ??
                            proposal.batch?.action ??
                            "—"}
                        </p>
                        <p>Supplier: {proposal.supplier?.action ?? "—"}</p>
                      </td>
                      <td className="px-5 py-3">
                        {proposal.requiresDecision ? (
                          <div
                            className="flex flex-wrap gap-2"
                            role="group"
                            aria-label={`Decision for ${proposal.sourceCoordinates}`}
                          >
                            {(["MERGED", "IGNORED", "REJECTED"] as const).map(
                              (choice) => (
                                <button
                                  key={choice}
                                  type="button"
                                  onClick={() =>
                                    setDecisions((current) => ({
                                      ...current,
                                      [proposal.rowId]: choice,
                                    }))
                                  }
                                  className={`rounded-lg border px-3 py-1.5 text-xs font-bold ${decisions[proposal.rowId] === choice ? "border-blue-600 bg-blue-600 text-white" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"}`}
                                >
                                  {choice === "MERGED"
                                    ? "Merge"
                                    : choice === "IGNORED"
                                      ? "Ignore"
                                      : "Reject"}
                                </button>
                              ),
                            )}
                          </div>
                        ) : proposal.proposedAction === "MERGE_AUTO" ? (
                          <span className="text-xs font-semibold text-emerald-700">
                            Auto-merge — unique exact identifier
                          </span>
                        ) : (
                          <span className="text-xs text-slate-500">
                            No decision required
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => void reconcile()}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-blue-700"
            >
              <GitMerge className="h-4 w-4" /> Calculate reconciliation
            </button>
          </div>
        </section>
      )}

      {view === "reconcile" && reconciliation && (
        <section className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <Metric label="Source rows" value={reconciliation.rows.source} />
            <Metric
              label="Imported quantity"
              value={reconciliation.quantity.proposedLive}
            />
            <Metric
              label="Expired tracked"
              value={reconciliation.quantity.expiredTracked}
            />
            <Metric
              label="Proposed value"
              value={formatPaise(reconciliation.valuePaise.proposedLive)}
            />
            <Metric
              label="Value variance"
              value={formatPaise(reconciliation.valuePaise.variance)}
            />
          </div>
          <div className="grid gap-5 lg:grid-cols-[1fr_0.8fr]">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-bold text-slate-900">
                Reconciliation summary
              </h2>
              <div className="mt-5 overflow-hidden rounded-xl border border-slate-200">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                    <tr>
                      <th className="px-4 py-3">Measure</th>
                      <th className="px-4 py-3">Source</th>
                      <th className="px-4 py-3">Proposed import</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    <tr>
                      <td className="px-4 py-3 font-semibold">Rows</td>
                      <td className="px-4 py-3">
                        {reconciliation.rows.source}
                      </td>
                      <td className="px-4 py-3">
                        {reconciliation.rows.accepted}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-semibold">Products</td>
                      <td className="px-4 py-3">
                        {reconciliation.products.source}
                      </td>
                      <td className="px-4 py-3">
                        {reconciliation.products.proposedLive}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-semibold">Batches</td>
                      <td className="px-4 py-3">
                        {reconciliation.batches.source}
                      </td>
                      <td className="px-4 py-3">
                        {reconciliation.batches.proposedLive}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-semibold">Quantity</td>
                      <td className="px-4 py-3">
                        {reconciliation.quantity.source}
                      </td>
                      <td className="px-4 py-3">
                        {reconciliation.quantity.proposedLive}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-semibold">
                        Expired (non-saleable)
                      </td>
                      <td className="px-4 py-3">—</td>
                      <td className="px-4 py-3">
                        {reconciliation.quantity.expiredTracked}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-semibold">Value</td>
                      <td className="px-4 py-3">
                        {formatPaise(reconciliation.valuePaise.source)}
                      </td>
                      <td className="px-4 py-3">
                        {formatPaise(reconciliation.valuePaise.proposedLive)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <button
                type="button"
                onClick={() =>
                  jobId &&
                  void productRevivalImportService.downloadReconciliation(jobId)
                }
                className="mt-4 inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-bold hover:bg-slate-50"
              >
                <Download className="h-4 w-4" /> Download row drilldown
              </button>
            </div>
            <aside className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6">
              <ShieldCheck className="h-9 w-9 text-emerald-700" />
              <h2 className="mt-4 text-lg font-bold text-slate-900">
                Ready to activate
              </h2>
              <p className="mt-2 text-sm leading-6 text-slate-700">
                Activation writes catalog records, opening movements, audit
                events, and one outbox event in a single transaction after your
                final confirmation. A retry cannot duplicate them.
              </p>
              <button
                type="button"
                disabled={Boolean(busy)}
                onClick={() => void commit()}
                className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-700 px-4 py-2.5 text-sm font-bold text-white hover:bg-emerald-800 disabled:opacity-60"
              >
                <Database className="h-4 w-4" /> Activate import
              </button>
            </aside>
          </div>
        </section>
      )}

      {view === "result" && job && (
        <section className="space-y-5">
          <div
            className={`rounded-2xl border p-8 text-center ${job.status === "REVERSED" ? "border-slate-300 bg-slate-50" : "border-emerald-200 bg-emerald-50"}`}
          >
            <span
              className={`mx-auto flex h-16 w-16 items-center justify-center rounded-full ${job.status === "REVERSED" ? "bg-slate-200 text-slate-700" : "bg-emerald-600 text-white"}`}
            >
              {job.status === "REVERSED" ? (
                <RotateCcw className="h-8 w-8" />
              ) : (
                <Check className="h-8 w-8" />
              )}
            </span>
            <h2 className="mt-5 text-2xl font-bold text-slate-950">
              {job.status === "REVERSED"
                ? "Import safely reversed"
                : "Import activated"}
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              Job {job.id} · {job.file.originalFilename}
            </p>
            {result?.importedQuantity && (
              <p className="mt-4 text-lg font-semibold text-emerald-800">
                {result.importedQuantity} base units across{" "}
                {result.importedLots} batches
              </p>
            )}
            {result?.replayed && (
              <p className="mt-2 text-sm font-semibold text-blue-700">
                This was a safe replay; no duplicate records were created.
              </p>
            )}
          </div>
          {job.status === "ACTIVATED" && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 text-amber-700" />
                <div>
                  <h3 className="font-bold text-amber-950">Owner undo</h3>
                  <p className="mt-1 text-sm text-amber-800">
                    Undo is refused if any imported lot has downstream
                    transactions. Successful undo adds compensating movements
                    and keeps history.
                  </p>
                </div>
              </div>
              <label
                className="mt-4 block text-sm font-semibold text-amber-950"
                htmlFor="undo-reason"
              >
                Reason
              </label>
              <div className="mt-2 flex flex-col gap-3 sm:flex-row">
                <input
                  id="undo-reason"
                  value={undoReason}
                  onChange={(event) => setUndoReason(event.target.value)}
                  placeholder="Why is this activation being reversed?"
                  className="flex-1 rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  onClick={() => void undo()}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-amber-700 px-4 py-2 text-sm font-bold text-amber-900 hover:bg-amber-100"
                >
                  <RotateCcw className="h-4 w-4" /> Undo import
                </button>
              </div>
            </div>
          )}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={reset}
              className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold hover:bg-slate-50"
            >
              Import another file
            </button>
            <button
              type="button"
              onClick={() => navigate("/products")}
              className="rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-blue-700"
            >
              View products
            </button>
          </div>
        </section>
      )}

      {editingRow && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="correction-title"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/60 p-4"
        >
          <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2
                  id="correction-title"
                  className="text-xl font-bold text-slate-950"
                >
                  Correct staged row
                </h2>
                <p className="mt-1 text-sm text-slate-600">
                  {editingRow.sourceCoordinates}. Raw source values remain
                  immutable.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setEditingRow(null)}
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-bold"
              >
                Close
              </button>
            </div>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {correctionFields.map(([key, label, type]) => (
                <label
                  key={key}
                  className="text-sm font-semibold text-slate-700"
                >
                  {label}
                  <input
                    type={type}
                    step={type === "number" ? "any" : undefined}
                    value={correction[key] ?? ""}
                    onChange={(event) =>
                      setCorrection((current) => ({
                        ...current,
                        [key]: event.target.value,
                      }))
                    }
                    className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 font-normal text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                </label>
              ))}
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setEditingRow(null)}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-bold"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void saveCorrection()}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700"
              >
                Save & revalidate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
